

























































































































import { Component, Vue } from 'vue-property-decorator';

import { ValidationObserver, ValidationProvider } from 'vee-validate-3';

import HTTPStatus from '@/constants/httpStatus';

import SignInService from '@/api/SigninService';
import ManagerService from '@/api/ManagerService';
import TextInput from '@/components/TextInput/TextInput.vue';
import FlexButton from '@/components/FlexButton/FlexButton.vue';
import Typography from '@/components/atoms/Typography/Typography.vue';
import Input from '@/componentsV2/molecules/Inputs/TextField.vue';
import Picture from '@/components/atoms/Picture/Picture.vue';
import FilledButton from '@/components/molecules/FilledButton/FilledButton.vue';
import ProviderButton from '@/components/molecules/ProviderButton/ProviderButton.vue';
import { InitialSuperAdminPath } from '@/constants/routes';
import flagKeys from '@/constants/flagKeys';
import isMobile from '@/utils/mobile-check';
import segmentIo from '@/utils/segment.io';
import APIService from '@/api';
import SignUpService from '@/api/SingupService';

import { isOauthSession } from '../../router/router-utils';
import { setFocusOnFirstInput } from '@/utils/setFocusOnFirstInput';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { getSignUpUrl } from '../Onboarding/helpers';
import logger from '@/utils/logger';
import { REDIRECT_LINK_STORAGE } from '@/router/middlewares/HandleSigninRedirectRestrictedPages';
import { getMerchantInviteState } from '@/router/router-utils';
import { OnboardingURL, getSignUpUrlRoutes } from '@/views/Onboarding/helpers';
import { sanitizeHtml } from '@/utils/sanitizeHtml';
import { getLoginNoticeI18nKey } from '@/utils/loginNotice';
import { takeLoginPrefillEmail } from '@/utils/loginPrefillEmail';

interface SubmitEvent extends Event {
  submitter: HTMLElement;
}

@Component({
  components: {
    TextInput,
    FlexButton,
    FilledButton,
    Typography,
    Input,
    ProviderButton,
    Picture,
    ValidationObserver,
    ValidationProvider
  }
})
export default class Login extends Vue {
  loading: boolean = false;
  showContactUsMessage: boolean = false;
  email: string = '';
  password: string = '';
  error: string = '';
  service: SignInService = new SignInService();
  signUpService = new SignUpService();
  managerService: ManagerService = new ManagerService();
  targetDate: Date = new Date();
  countdown: string | null = null;
  timer: NodeJS.Timeout | null = null;

  $refs!: {
    observer: InstanceType<typeof ValidationObserver>;
  };

  get hasEmailQueryParam() {
    return !!this.$route.query.email;
  }

  get isAxxessFlow() {
    return (
      this.$route.query.utm_source && this.$route.query.utm_source === 'axxess'
    );
  }

  get isMerchantInviteFlow() {
    return (
      this.$route.query.utm_source &&
      this.$route.query.utm_source === 'hubspot-merchant-invite'
    );
  }

  get formCopies() {
    if (this.isMerchantInviteFlow) {
      const description =
        this.$route.query.managerType &&
        this.$route.query.managerType === 'dashboard'
          ? 'You already have an account on Twism.'
          : 'You already have a Twism App account.';
      return {
        title: 'Welcome!',
        description: `${description} Use the same credentials below to log in and access the Merchant Portal`
      };
    }

    if (this.isAxxessFlow) {
      return {
        title: 'Welcome to Twism!',
        description: 'Log in to access your offers'
      };
    }

    return {
      title: 'Hi there, welcome back!',
      description: ''
    };
  }

  mounted() {
    this.verifyErrorQuery();
    logger.reset();
    if (segmentIo.getIdentifiedId()) segmentIo.reset();
  }

  created() {
    if (this.isMerchantInviteFlow) {
      this.getDataFromInvite();
      return;
    }
    this.getDataFromQuery();
  }

  verifyErrorQuery() {
    const { error, notice } = this.$route.query;

    const noticeKey = getLoginNoticeI18nKey(notice);
    if (noticeKey) {
      this.$notification(String(this.$t(noticeKey)), 'info');
      this.$router.replace({ query: {} });
    }

    if (error) {
      if (typeof error === 'string') {
        const safeError = sanitizeHtml(error);
        this.$notification(safeError, 'error');
      }

      this.$router.replace({ query: {} });
    }
  }

  redirectToContact(): void {
    window.location.href = 'https://support.twism.com/';
  }

  submit(event: SubmitEvent) {
    const id = event && event.submitter ? event.submitter.id : '';

    switch (id) {
      case 's-google':
        this.providerSignin('google');
        break;

      case 's-apple':
        this.providerSignin('apple');
        break;

      default:
        this.emailSignin();
        break;
    }
  }

  startCountdown(timeLeft: string) {
    this.error = '';
    this.targetDate = new Date(timeLeft);

    this.destroyTimer();

    this.timer = setInterval(() => {
      const currentDate = new Date();
      const difference = this.targetDate.getTime() - currentDate.getTime();
      const totalSeconds = Math.floor(difference / 1000) + 10;
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;

      if (minutes > 5) {
        this.showContactUsMessage = true;
        this.destroyTimer();
      } else if (minutes <= 0 && seconds <= 0) {
        this.countdown = '0:00';
        this.error = '';
        this.destroyTimer();
      } else {
        this.countdown = `${minutes}:${seconds.toString().padStart(2, '0')}`;
        this.error = `You have reached your attempt limit. Try again in ${
          this.countdown
        } minutes.`;
      }
    }, 1000);
  }

  destroyTimer() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  getErrorMessageWithSignInLimit(responseData: any) {
    const translation = responseData.message[0].translation;

    if (translation.attempt && translation.attemptTotal) {
      return 'This password is incorrect. Please try again.';
    }

    if (translation.timeLeft) {
      this.error = '';
      this.startCountdown(translation.timeLeft);
      return;
    }

    return getErrorMessage(responseData.message);
  }

  async emailSignin() {
    const formRef = this.$refs.observer;
    const formIsValid = await formRef.validate();

    this.error = '';
    this.showContactUsMessage = false;
    this.destroyTimer();

    if (!formIsValid) {
      setFocusOnFirstInput('input[aria-invalid="true"]');

      return;
    }

    const { email, password } = this;
    const authType = this.isSuperAdmin ? 'super-admin' : 'manager';

    try {
      this.loading = true;
      const response: any = await this.service.login(
        password,
        email.trim(),
        authType
      );

      if (
        response.response &&
        response.response.data &&
        response.response.data.message
      ) {
        const responseData = response.response.data;
        const errorMessage = this.getFlags.includes(flagKeys.enableSignInLimit)
          ? this.getErrorMessageWithSignInLimit(responseData)
          : getErrorMessage(responseData.message);

        this.error = this.isAxxessFlow
          ? String(this.$t('common.signIn.errorCredentials'))
          : errorMessage;

        return;
      }

      // Cookie is already set by the backend in the signin response.
      // No need to store the token — auth is now cookie-based.

      if (this.$route.params.inviteCode) {
        const { status } = await this.managerService.acceptInvite(
          this.$route.params.inviteCode
        );

        if (status === HTTPStatus.OK) {
          // Backend sets a new cookie; nothing to store locally
        }
      }

      if (isOauthSession()) {
        this.$router.push({
          name: 'merchant-connect-oauth',
          query: {
            cb: 'external'
          }
        });
      } else if (this.isMerchantInviteFlow) {
        const route = getSignUpUrlRoutes() as typeof OnboardingURL;
        window.location.assign(route.INVITATION_MERCHANT_SIGNUP);
      } else if (this.$route.params.redirect) {
        window.location.assign(this.$route.params.redirect);
      } else {
        if (this.isSuperAdmin) {
          window.location.assign(InitialSuperAdminPath);
        } else {
          const link = this.getRedirectToUrl();
          window.location.assign(link);
        }
      }

      localStorage.removeItem(REDIRECT_LINK_STORAGE);

      if (this.isSuperAdmin) {
        localStorage.removeItem(
          `${window.location.host}-${APIService.TOKEN_KEY}`
        );
      } else {
        localStorage.removeItem(
          `${window.location.host}-${APIService.SUPER_ADMIN}`
        );
      }
    } finally {
      setTimeout(() => {
        this.loading = false;
      }, 2500);
    }
  }

  getRedirectToUrl() {
    const redirectLinkStorage = localStorage.getItem(REDIRECT_LINK_STORAGE);
    if (redirectLinkStorage) {
      return redirectLinkStorage;
    }

    const query = { ...this.$route.query } as Record<string, any>;
    const host = query.redirect_to || '/';

    delete query.redirect_to;

    const queryParam = new URLSearchParams();

    for (const property in query) {
      queryParam.set(property, query[property]);
    }

    const parsedQueryParam = queryParam.toString();
    const search = parsedQueryParam ? `?${parsedQueryParam}` : '';
    return `${host}${search}`;
  }

  providerSignin(name: 'google' | 'apple') {
    const link = this.getRedirectToUrl();
    this.service.loginWithProvider(name, {
      ...this.$route.params,
      ...(this.$route.query.redirect_to
        ? {
            redirect: link
          }
        : {})
    });
    localStorage.removeItem(REDIRECT_LINK_STORAGE);
  }

  goToSignup() {
    this.$router.push({ path: getSignUpUrl() });
  }

  getDataFromQuery() {
    const prefillEmail = takeLoginPrefillEmail();
    const email =
      this.$route.params.email || this.$route.query.email || prefillEmail;

    if (email) this.email = email as string;
  }

  getDataFromInvite() {
    const { managers } = getMerchantInviteState();

    if (!managers[0] || !managers[0].manager || !managers[0].manager.mail)
      return;

    this.email = managers[0].manager.mail;
  }

  get isMobile() {
    return isMobile();
  }

  get showSignUpLink() {
    return !this.isSuperAdmin && !this.isMerchantInviteFlow;
  }

  get isSuperAdmin() {
    return this.$store.getters['business/isRootView'];
  }

  get getFlags() {
    return this.$store.getters['flags/getFlags'];
  }

  verifyFlag(flag: string) {
    return this.getFlags.includes(flag);
  }

  beforeDestroy() {
    this.destroyTimer();
  }
}
